import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import workflowApi from '../../features/workflow/services/workflowApi';

/**
 * Build the conversation key used to index conversations in state.
 * @param {number} workflowId
 * @param {string} stageType
 * @param {number} userId
 * @returns {string}
 */
export const buildConversationKey = (workflowId, stageType, userId) =>
  `${workflowId}-${stageType}-${userId}`;

// ── Async Thunks ──────────────────────────────────────────────────────────────

/**
 * Start an SSE streaming generation via the WorkflowController endpoint.
 * POST /api/workflows/{workflowId}/stages/{stageType}/generate-stream
 *
 * Used by the "Generate with Agent" button. The ConversationPanel uses
 * the ChatController SSE endpoint directly for chat-based streaming.
 */
export const generateStream = createAsyncThunk(
  'workflowConversation/generateStream',
  async ({ workflowId, stageType, prompt, contextFiles, conversationHistory }, { dispatch }) => {
    const callbacks = await workflowApi.generateStream(
      workflowId,
      stageType,
      prompt,
      contextFiles,
      conversationHistory,
    );

    // Wrap in a promise so we can await the full stream and return the content
    return new Promise((resolve, reject) => {
      callbacks.onToken = (token) => {
        dispatch(appendStreamToken(token));
      };

      callbacks.onStepProgress = (step) => {
        dispatch(addStreamStep(step));
      };

      let insufficiency = null;
      callbacks.onInsufficient = (message) => {
        insufficiency = message;
      };

      callbacks.onDone = (parsed, fullContent) => {
        if (parsed?.insufficient || insufficiency) {
          // The gate declined: no artifact was produced and nothing must be
          // written into the editor. Resolve with the explanation instead.
          dispatch(finalizeStream({ content: null, artifactId: null }));
          resolve({ content: null, insufficient: true, message: insufficiency });
          return;
        }
        dispatch(finalizeStream({ content: fullContent, artifactId: parsed?.artifactId }));
        resolve({ content: fullContent, artifactId: parsed?.artifactId });
      };

      callbacks.onError = (errorMsg) => {
        dispatch(streamError(errorMsg));
        reject(new Error(errorMsg));
      };
    });
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const workflowConversationSlice = createSlice({
  name: 'workflowConversation',
  initialState: {
    conversations: {},
    activeKey: null,
    streaming: false,
    // Set only by the generateStream lifecycle. `streaming` alone also covers
    // plain chat replies, and keying the Generate button off it made every
    // sent message look like a generation in progress.
    generating: false,
    streamingMessage: '',
    streamingSteps: [],
    pendingMessages: [],
  },
  reducers: {
    /** Append a single message to a conversation. */
    addMessage: (state, action) => {
      const { key, message } = action.payload;
      if (!state.conversations[key]) {
        state.conversations[key] = { messages: [], loading: false, error: null };
      }
      state.conversations[key].messages.push(message);
    },

    /** Replace the full message list for a conversation. */
    removeMessage: (state, action) => {
      const { key, messageId } = action.payload;
      const conv = state.conversations[key];
      if (conv) conv.messages = conv.messages.filter((m) => m.id !== messageId);
    },
    setMessages: (state, action) => {
      const { key, messages } = action.payload;
      if (!state.conversations[key]) {
        state.conversations[key] = { messages: [], loading: false, error: null };
      }
      state.conversations[key].messages = messages;
    },

    /** Toggle the streaming flag. */
    setStreaming: (state, action) => {
      state.streaming = action.payload;
    },

    /** Overwrite the current streaming message buffer. */
    setStreamingMessage: (state, action) => {
      state.streamingMessage = action.payload;
    },

    /** Append a token to the streaming message buffer. */
    appendStreamToken: (state, action) => {
      state.streamingMessage += action.payload;
    },

    /** Add a completed pipeline step to the streaming progress. */
    addStreamStep: (state, action) => {
      state.streamingSteps.push(action.payload);
    },

    /** Finalize the streamed message — push it into the conversation and reset streaming state. */
    finalizeStream: (state, action) => {
      const { content } = action.payload;
      if (state.activeKey && state.conversations[state.activeKey]) {
        state.conversations[state.activeKey].messages.push({
          id: `agent-${Date.now()}`,
          content,
          senderType: 'agent',
          timestamp: new Date().toISOString(),
        });
      }
      state.streaming = false;
      state.generating = false;
      state.streamingMessage = '';
      state.streamingSteps = [];
    },

    /** Handle a streaming error — reset streaming state and set error. */
    streamError: (state, action) => {
      state.streaming = false;
      state.generating = false;
      state.streamingMessage = '';
      state.streamingSteps = [];
      if (state.activeKey && state.conversations[state.activeKey]) {
        state.conversations[state.activeKey].error = action.payload;
      }
    },

    /** Add a message to the offline pending queue. */
    addPendingMessage: (state, action) => {
      state.pendingMessages.push(action.payload);
    },

    /** Remove a pending message by tempId after successful persistence. */
    removePendingMessage: (state, action) => {
      state.pendingMessages = state.pendingMessages.filter(
        (m) => m.tempId !== action.payload,
      );
    },

    /** Set loading state for a conversation. */
    setLoading: (state, action) => {
      const { key, loading } = action.payload;
      if (!state.conversations[key]) {
        state.conversations[key] = { messages: [], loading: false, error: null };
      }
      state.conversations[key].loading = loading;
    },

    /** Set error state for a conversation. */
    setError: (state, action) => {
      const { key, error } = action.payload;
      if (!state.conversations[key]) {
        state.conversations[key] = { messages: [], loading: false, error: null };
      }
      state.conversations[key].error = error;
    },

    /** Set the active conversation key. */
    setActiveKey: (state, action) => {
      state.activeKey = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // generateStream
      .addCase(generateStream.pending, (state) => {
        state.streaming = true;
        state.generating = true;
        state.streamingMessage = '';
        state.streamingSteps = [];
      })
      .addCase(generateStream.rejected, (state, action) => {
        state.streaming = false;
        state.generating = false;
        state.streamingMessage = '';
        state.streamingSteps = [];
        if (state.activeKey && state.conversations[state.activeKey]) {
          state.conversations[state.activeKey].error = action.error.message;
        }
      });
  },
});

export const {
  addMessage,
  removeMessage,
  setMessages,
  setStreaming,
  setStreamingMessage,
  appendStreamToken,
  addStreamStep,
  finalizeStream,
  streamError,
  addPendingMessage,
  removePendingMessage,
  setLoading,
  setError,
  setActiveKey,
} = workflowConversationSlice.actions;

export default workflowConversationSlice.reducer;
